import React, { useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
    attachClosestEdge,
    extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

import { removeTodayTask, toggleTodayTask } from '../../redux/Slices/todayTaskSlice';
import { updateCard } from '../../redux/Slices/cardSlice';
import { relative } from '../../utils/helpers';
import Brightness1 from '../icons/Brightness1';
import CircleCheck from '../icons/CircleCheck';
import Close from '../icons/Close';

const MyDayTaskItem = ({ task, onReorder }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const itemRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [closestEdge, setClosestEdge] = useState(null);

    const card = task.card;
    const listTitle = card?.actionList?.title || card?.actionListTitle || '';

    // DnD — whole item draggable
    useEffect(() => {
        const el = itemRef.current;
        if (!el) return;
        const cleanup = combine(
            draggable({ element: el,
                getInitialData: () => ({ taskId: task._id, type: 'MY_DAY_TASK', order: task.order }),
                onDragStart: () => setIsDragging(true),
                onDrop: () => { setIsDragging(false); setClosestEdge(null); },
            }),
            dropTargetForElements({ element: el,
                getData: ({ input, element }) => attachClosestEdge({ taskId: task._id, type: 'MY_DAY_TASK', order: task.order }, { input, element, allowedEdges: ['top', 'bottom'] }),
                canDrop: ({ source }) => source.data.type === 'MY_DAY_TASK',
                onDrag: ({ source, self }) => { if (source.data.taskId !== self.data.taskId) setClosestEdge(extractClosestEdge(self.data)); },
                onDragLeave: () => setClosestEdge(null),
                onDrop: ({ source, self }) => { setClosestEdge(null); if (source.data.taskId === self.data.taskId) return; const edge = extractClosestEdge(self.data); if (edge && onReorder) onReorder(source.data.taskId, self.data.taskId, edge); },
            })
        );
        return cleanup;
    }, [task._id, task.order, onReorder]);

    const handleToggle = (e) => {
        e.stopPropagation();
        const v = !task.completed;
        dispatch(toggleTodayTask(task._id));
        if (card?.shortId) {
            dispatch(updateCard({ cardId: card.shortId, updatedData: { completed: v } }));
            window.dispatchEvent(new CustomEvent('cardCompletionChanged', { detail: { shortId: card.shortId, completed: v } }));
        }
    };

    if (!card) return null;

    return (
        <div ref={itemRef} className={`myday-task-item ${isDragging ? 'dragging' : ''} ${task.completed ? 'completed' : ''}`}>
            {closestEdge === 'top' && <div className="myday-drop-indicator top" />}

            <div className="myday-task-checkbox" onClick={handleToggle}>
                {task.completed
                    ? <CircleCheck size={18} color="var(--secondary-brand-color)" />
                    : <Brightness1 size={18} />
                }
            </div>

            <div className="myday-task-content" onClick={() => card?.permalink && navigate(relative(card.permalink))}>
                <span className={`myday-task-title ${task.completed ? 'line-through' : ''}`}>{card.title}</span>
                {listTitle && <span className="myday-task-list-badge">{listTitle}</span>}
            </div>

            <button className="myday-task-remove" onClick={(e) => { e.stopPropagation(); dispatch(removeTodayTask(task._id)); }} title="Remove">
                <Close size={14} color="var(--secondary-text-color)" />
            </button>

            {closestEdge === 'bottom' && <div className="myday-drop-indicator bottom" />}
        </div>
    );
};

export default MyDayTaskItem;
